import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileSpreadsheet, Loader2, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { LinhaDetalhe } from '@/hooks/usePlantaVivoSnapshot';
import { supabase } from '@/integrations/supabase/client';

interface PlantaVivoUploaderProps {
  onImport: (dados: {
    total_linhas: number;
    valor_total: number;
    gb_total: number;
    linhas_detalhadas: LinhaDetalhe[];
    dados_brutos: Record<string, unknown>;
  }) => void;
  isLoading?: boolean;
  cnpjLead?: string;
  leadId?: string;
  razaoSocial?: string | null;
  compact?: boolean;
}

// Extrai GB do nome do plano
function extrairGB(descricaoPlano: string): number {
  if (!descricaoPlano) return 0;
  const match = descricaoPlano.match(/(\d+(?:[.,]\d+)?)\s*gb/i);
  if (match) {
    return parseFloat(match[1].replace(',', '.'));
  }
  return 0;
}

// Normaliza telefone para 11 dígitos
function normalizarTelefone(telefone: string | number | undefined): string {
  if (!telefone) return '';
  const digits = String(telefone).replace(/\D/g, '');
  if (digits.length >= 10 && digits.length <= 11) {
    return digits;
  }
  return digits;
}

// Processa valor monetário
function parseValor(valor: any): number {
  if (typeof valor === 'number') return valor;
  if (!valor) return 0;
  const str = String(valor).replace(/[^\d,.-]/g, '').replace(',', '.');
  return parseFloat(str) || 0;
}

// Processa meses de fidelidade
function parseMeses(m: any): number | null {
  if (m === null || m === undefined || m === '') return null;
  const num = parseInt(String(m));
  return isNaN(num) ? null : num;
}

// Sanitiza razão social para nome de arquivo
function sanitizarNome(nome: string): string {
  return nome
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .replace(/[^A-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 40);
}

export function PlantaVivoUploader({ onImport, isLoading, cnpjLead, leadId, razaoSocial, compact = false }: PlantaVivoUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      // Buscar aba "Linhas com Receita" ou primeira aba
      const sheetNameLinhas = workbook.SheetNames.find(
        name => name.toLowerCase().includes('linhas') || name.toLowerCase().includes('receita')
      ) || workbook.SheetNames[0];

      const linhasSheet = workbook.Sheets[sheetNameLinhas];
      const linhasRaw: Record<string, any>[] = XLSX.utils.sheet_to_json(linhasSheet);

      if (!linhasRaw.length) {
        throw new Error('Planilha vazia ou formato não reconhecido');
      }

      // Buscar aba "Serviços por Linha" se existir
      const sheetNameServicos = workbook.SheetNames.find(
        name => name.toLowerCase().includes('servi') || name.toLowerCase().includes('service')
      );

      let servicosMap: Map<string, string[]> = new Map();
      if (sheetNameServicos) {
        const servicosSheet = workbook.Sheets[sheetNameServicos];
        const servicosRaw: Record<string, any>[] = XLSX.utils.sheet_to_json(servicosSheet);
        
        servicosRaw.forEach(row => {
          const telefone = normalizarTelefone(row.Telefone || row.telefone || row.TELEFONE);
          const servico = row.DescricaoServico || row.descricaoservico || row.DESCRICAOSERVICO || '';
          
          if (telefone && servico) {
            const existing = servicosMap.get(telefone) || [];
            if (!existing.includes(servico)) {
              existing.push(servico);
            }
            servicosMap.set(telefone, existing);
          }
        });
      }

      // Mapear colunas flexíveis
      const getField = (row: Record<string, any>, ...keys: string[]): any => {
        for (const key of keys) {
          if (row[key] !== undefined) return row[key];
          const lowerKey = key.toLowerCase();
          for (const k of Object.keys(row)) {
            if (k.toLowerCase() === lowerKey) return row[k];
          }
        }
        return undefined;
      };

      // Processar linhas
      const linhasProcessadas: LinhaDetalhe[] = [];
      const telefonesVistos = new Set<string>();

      for (const row of linhasRaw) {
        const telefone = normalizarTelefone(
          getField(row, 'Telefone', 'telefone', 'TELEFONE', 'nr_telefone', 'NR_TELEFONE')
        );
        
        if (!telefone || telefonesVistos.has(telefone)) continue;
        telefonesVistos.add(telefone);

        const plano = String(getField(row, 'DescricaoPlano', 'descricaoplano', 'DESCRICAOPLANO', 'ds_plano', 'plano') || '');
        const valor = parseValor(getField(row, 'ValorReceita', 'valorreceita', 'VALORRECEITA', 'valor', 'vl_receita'));
        const m = parseMeses(getField(row, 'MLinha', 'mlinha', 'MLINHA', 'm', 'M'));
        const estado = String(getField(row, 'EstadoLinha', 'estadolinha', 'ESTADOLINHA', 'estado', 'status') || 'Ativo');
        const gb = extrairGB(plano);

        const servicos = servicosMap.get(telefone) || [];

        linhasProcessadas.push({
          telefone,
          plano,
          gb,
          valor,
          m,
          estado,
          servicos,
        });
      }

      if (!linhasProcessadas.length) {
        throw new Error('Nenhuma linha válida encontrada na planilha');
      }

      // Calcular totais
      const total_linhas = linhasProcessadas.length;
      const valor_total = linhasProcessadas.reduce((sum, l) => sum + l.valor, 0);
      const gb_total = linhasProcessadas.reduce((sum, l) => sum + l.gb, 0);

      // Verificar CNPJ se disponível
      const cnpjExcel = String(getField(linhasRaw[0], 'Documento', 'documento', 'DOCUMENTO', 'cnpj', 'CNPJ', 'nr_cnpj') || '').replace(/\D/g, '');
      const cnpjLeadLimpo = cnpjLead?.replace(/\D/g, '') || '';

      if (cnpjExcel && cnpjLeadLimpo && cnpjExcel !== cnpjLeadLimpo) {
        throw new Error(
          `CNPJ do arquivo Excel (${cnpjExcel}) não corresponde ao CNPJ do lead (${cnpjLeadLimpo}). ` +
          `Verifique se está importando o arquivo correto.`
        );
      }

      // --- Upload do arquivo original para o Storage ---
      let arquivo_url: string | null = null;
      let nome_arquivo: string | null = null;

      if (leadId && cnpjLeadLimpo) {
        try {
          const now = new Date();
          const dd = String(now.getDate()).padStart(2, '0');
          const mm = String(now.getMonth() + 1).padStart(2, '0');
          const yyyy = now.getFullYear();
          const dataSufixo = `${dd}${mm}${yyyy}`;

          const razaoSanitizada = razaoSocial ? sanitizarNome(razaoSocial) : 'EMPRESA';
          nome_arquivo = `${cnpjLeadLimpo}_${razaoSanitizada}_${dataSufixo}.xlsx`;

          const storagePath = `planta-vivo/${leadId}/${nome_arquivo}`;

          // Converter arrayBuffer de volta para Blob
          const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('faturas')
            .upload(storagePath, blob, { upsert: true });

          if (uploadError) {
            console.warn('Erro ao fazer upload da planta Vivo para Storage:', uploadError.message);
          } else {
            arquivo_url = uploadData?.path || storagePath;
          }
        } catch (uploadErr) {
          console.warn('Upload do arquivo para Storage falhou (não bloqueante):', uploadErr);
        }
      }

      // Dados brutos para referência
      const dados_brutos: Record<string, unknown> = {
        arquivo: file.name,
        processado_em: new Date().toISOString(),
        aba_linhas: sheetNameLinhas,
        aba_servicos: sheetNameServicos || null,
        total_linhas_raw: linhasRaw.length,
        cnpj_detectado: cnpjExcel || null,
        arquivo_url: arquivo_url || null,
        nome_arquivo: nome_arquivo || null,
      };

      onImport({
        total_linhas,
        valor_total,
        gb_total,
        linhas_detalhadas: linhasProcessadas,
        dados_brutos,
      });

    } catch (err: any) {
      console.error('Erro ao processar Excel:', err);
      setError(err.message || 'Erro ao processar arquivo');
    } finally {
      setProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={compact ? 'space-y-2' : 'flex flex-col items-center justify-center space-y-4 py-6 text-center'}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleFileSelect}
        disabled={processing || isLoading}
      />

      {compact ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-left">
          <div className="flex min-w-0 items-center gap-2.5">
            <FileSpreadsheet className="h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground">Atualizar Planta Vivo</p>
              <p className="truncate text-[10px] text-muted-foreground">Importe o Excel da Vivo para atualizar as linhas.</p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={processing || isLoading}
            className="h-8 shrink-0 gap-1.5 px-3 text-[11px]"
          >
            {processing || isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {processing || isLoading ? 'Processando...' : 'Importar Excel'}
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-full bg-muted p-4">
            <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium">Importe a Planta Vivo</p>
            <p className="max-w-xs text-xs text-muted-foreground">
              Selecione o arquivo Excel (.xlsx) exportado da Vivo contendo as linhas do cliente
            </p>
          </div>
        </>
      )}

      {error && (
        <Alert variant="destructive" className="max-w-sm">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!compact && (
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={processing || isLoading}
          className="gap-2"
        >
          {processing || isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Processando...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              Importar Planta Vivo Excel
            </>
          )}
        </Button>
      )}
    </div>
  );
}
